import { differenceInCalendarDays, isToday } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import React from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";

import { useComplicatedRolesAndStatuses } from "hooks/useComplicatedRolesAndStatuses";
import {
  setCurrentPastExamId,
  setIsEditPastExamFormOpen
} from "store/actions/vetExam";
import { transformViewDate } from "utils/date-utils";

import useStyles from "./styles";

interface IProps {
  exam: racehorse360.IHorseExam;
}

const ALLOWED_EDIT_DAYS = 5;

const ExamActions = (props: IProps) => {
  const { exam } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { isCurrentUserChiefOrSeniorVet, isCurrentUserAssignedTrackVet } =
    useComplicatedRolesAndStatuses({
      assignedUserId: exam.assignedUser?.id
    });

  const handleOpenEditPastExamForm = () => {
    dispatch(setCurrentPastExamId(exam.id));
    dispatch(setIsEditPastExamFormOpen(true));
  };

  const examTimeZone = exam.facility?.timezone;

  const todayZoned = utcToZonedTime(new Date(), examTimeZone);
  const examDateCompletedZoned = utcToZonedTime(
    new Date(exam.completedDate),
    examTimeZone
  );
  const daysNumberAfterCompletion = differenceInCalendarDays(
    todayZoned,
    examDateCompletedZoned
  );

  const isRaceDayExam =
    exam.reason?.code ===
    racehorse360.HorseExamReasonCode.HORSE_EXAM_REASON_CODE_RACE_DAY_EXAM;
  const hasRaceEntry = Boolean(exam.upcomingRaceEntry);
  const isRaceDayExamCompletedToday = isToday(examDateCompletedZoned);
  const hasExceededEditLimit = daysNumberAfterCompletion > ALLOWED_EDIT_DAYS;

  const canEditRaceDayExam =
    isRaceDayExam && hasRaceEntry && isRaceDayExamCompletedToday;
  const canEditOtherExams = !isRaceDayExam && !hasExceededEditLimit;

  const shouldShowActionButtons =
    (isCurrentUserAssignedTrackVet || isCurrentUserChiefOrSeniorVet) &&
    (canEditRaceDayExam || canEditOtherExams);

  const examDateEdited = transformViewDate(
    exam.recompletedOn,
    examTimeZone,
    "MM/dd/yy"
  );
  const examTimeEdited = transformViewDate(
    exam.recompletedOn,
    examTimeZone,
    "p"
  );
  const infoContent = `Last edited by Dr. ${exam.updatedBy?.lastName} on ${examDateEdited} at ${examTimeEdited}`;

  return (
    <div className={classes.examActions}>
      {Boolean(exam.recompletedOn) && (
        <span className={classes.editionInfo}>{infoContent}</span>
      )}
      {shouldShowActionButtons && (
        <div className={classes.actions}>
          <IconButton
            className={classes.editButton}
            onClick={handleOpenEditPastExamForm}
          >
            <EditIcon className={classes.editButtonIcon} />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default React.memo(ExamActions);
